import React, { useEffect, useState, useCallback } from "react";

// MUI
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import InfoIcon from "@mui/icons-material/Info";
import { CSVLink } from "react-csv";

// Components
import Loader from "../../components/loader/Loader";
import PageSearch from "../PageSearch";
import EnhancedTable from "../table/Table";
import DialogBox from "../../components/dialog-box/DialogBox";
import ProjectHistoryData from "./ProjectHistoryData";

// Services
import ProjectService from "../../services/ProjectService";

// Utils
import { displayDateFormart, isValidArr } from "../../utility/utils";

type Props = {
  projectId: number;
  type?: string;
};

type HistoryModel = {
  id: number;
  projectId: number;
  userName: string;
  role: string;
  type: string;
  date: string;
  data?: any;
  historyData?: string;
};

const columns = [
  {
    id: "index",
    numeric: false,
    disablePadding: false,
    label: "#",
    width: 50,
  },
  {
    id: "userName",
    numeric: false,
    disablePadding: false,
    label: "User",
    width: 150,
  },
  {
    id: "role",
    numeric: false,
    disablePadding: false,
    label: "Role",
    width: 150,
  },
  {
    id: "type",
    numeric: false,
    disablePadding: false,
    label: "Type",
    width: 150,
  },
  {
    id: "date",
    numeric: false,
    disablePadding: false,
    label: "Date",
    width: 180,
  },
  {
    id: "data",
    numeric: false,
    disablePadding: false,
    label: "Data",
    width: 100,
  },
];

const ProjectHistory = ({ projectId, type }: Props) => {
  const [filteredData, setFilteredData] = useState<HistoryModel[]>([]);
  const [history, setHistory] = useState<HistoryModel[]>([]);
  const [historyId, setHistoryId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [csvData, setCsvData] = useState<(string | number | undefined)[][]>([]);
   
  useEffect(() => {
    function handleHistory(id: number) {
      setHistoryId(id);
      setIsModalOpen(true);
    }

    async function getHistory(id: number, historyType?: string) {
      try {
        setIsLoading(true);
        const response: HistoryModel[] = await ProjectService.getProjectHistory(
          id,
          historyType
        );
        if (isValidArr(response)) {
          setHistory(() => {
            return response.map((rec, index) => ({
              id: rec.id,
              index: index + 1,
              projectId: rec.projectId,
              userName: rec.userName,
              role: rec.role,
              type: rec.type,
              date: displayDateFormart(rec.date, "YYYY-MM-DD HH:mm"),
              data: (
                <IconButton
                  aria-label="history info"
                  onClick={() => handleHistory(rec.id)}
                >
                  <InfoIcon />
                </IconButton>
              ),
            }));
          });
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.log(error);
      }
    }

    getHistory(projectId, type);
  }, [projectId, type]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(history);
  }, [history]);

  useEffect(() => {
    const exportToCSV = async (id: number) => {
      const projectHistory: HistoryModel[] = await ProjectService.getProjectHistory(id, "null", true);
      const csvHistory = projectHistory.map(({id, projectId, userName, role, type, date, historyData}) => [
        id,
        projectId,
          userName,
          role,
          type,
          date,
          historyData
      ])
      setCsvData([
          ["ID", "ProjectId", "UserName", "Role", "Type", "Date", "Data"],
          ...csvHistory
      ]);
    }
    if(projectId) {
      exportToCSV(projectId)
    }
  }, [projectId, setCsvData]);

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? history.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : history
    );
  };

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <Box m={2}>
      {isLoading ? <Loader /> : null}
      {(csvData.length > 1) && <CSVLink className="downloadbtn" filename={`Project-${projectId}-History`} data={csvData}>
        Export to CSV
      </CSVLink>}
      <PageSearch placeholder="Search User" searchHandler={searchHandler} />
      <Typography variant="body2">Total Rows: {history.length}</Typography>
      <EnhancedTable columns={columns} data={filteredData} />
      {isModalOpen && historyId ? (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle="Data"
          handleModalClose={handleModalClose}
        >
          <ProjectHistoryData
            historyId={historyId}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      ) : null}
    </Box>
  );
};

export default ProjectHistory;
